import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { makeGetRequest, makePostRequest } from '../../../utils/api'
import urls from '../../../utils/urls'
import CreateOperationForm from './CreateOperationForm'
import EditOperationForm from './EditOperationForm'
import ParcoursPro from './ParcoursPro'
import { buildCvHtml, openCvPrintWindow } from './exportCv'
import { buildDuplicatePayload, mapParcoursPro } from './mapParcoursPro'

export const MonParcoursPro = () => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const location = useLocation()
  const navigate = useNavigate()
  const siteUser = useSelector((store) => store.user)
  const [operationsL1, setOperationsL1] = useState({})
  const [operationsL2, setOperationsL2] = useState({})
  const [operationsL3, setOperationsL3] = useState({})
  const [selectedRowsParams, setSelectedRowsParams] = useState({})
  const [selected, setSelected] = useState(false)

  // Ouverture du formulaire de création (bouton "Ajouter" ou ?action=new-experience)
  const [createOpen, setCreateOpen] = useState(false)
  const [createType, setCreateType] = useState('Expérience')

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const urlAction = searchParams.get('action') || searchParams.get('modal')

    if (urlAction === 'new-experience') {
      setCreateType('Expérience')
      setCreateOpen(true)
    }
  }, [location.search])

  const fetchdata = () => {
    fetchList1()
    fetchList2()
    fetchList3()
  }

  useEffect(() => {
    fetchdata()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchList1 = async (pagination = '') => {
    let url = `${urls.crudParcoursProOperations}?ParcoursType=Expérience&limit=100`

    if (pagination !== '') {
      url += pagination
    }
    const response = await makeGetRequest(url, {}, true)
    if (response.status === 200) {
      setOperationsL1({ ...response.data })
    }
  }
  const fetchList2 = async (pagination = '') => {
    let url = `${urls.crudParcoursProOperations}?ParcoursType=Formation&limit=100`

    if (pagination !== '') {
      url += pagination
    }
    const response = await makeGetRequest(url, {}, true)
    if (response.status === 200) {
      setOperationsL2({ ...response.data })
    }
  }
  const fetchList3 = async (pagination = '') => {
    let url = `/api/Professional/userCompetence/?limit=100000`

    if (pagination !== '') {
      url += pagination
    }
    const response = await makeGetRequest(url, {}, true)
    if (response.status === 200) {
      setOperationsL3({ count: response.data.length, results: response.data })
    }
  }

  // Modèle API → props du composant ParcoursPro (mapping pur, testé)
  const mapped = useMemo(
    () =>
      mapParcoursPro({
        experiences: operationsL1.results ?? [],
        formations: operationsL2.results ?? [],
        skills: operationsL3.results ?? [],
      }),
    [operationsL1, operationsL2, operationsL3],
  )

  // ── Callbacks branchés sur la logique existante ──────────────────

  const handleAdd = (kind) => {
    if (kind === 'experience') {
      setCreateType('Expérience')
      setCreateOpen(true)
    } else if (kind === 'formation') {
      setCreateType('Formation')
      setCreateOpen(true)
    } else {
      // Les compétences sont rattachées aux expériences (KeyCompetencies) :
      // pas de création directe pour l'instant.
      console.warn('TODO ParcoursPro: création directe de compétence non disponible (passer par une expérience)')
    }
  }

  const findOperation = (id) =>
    (operationsL1.results ?? []).find((e) => e.id === id) ??
    (operationsL2.results ?? []).find((e) => e.id === id)

  const handleEdit = (kind, id) => {
    if (kind === 'skill') {
      console.warn('TODO ParcoursPro: édition de compétence non disponible')
      return
    }
    const obj = findOperation(id)
    if (obj) {
      setSelectedRowsParams(obj)
      setSelected(true)
    }
  }

  const handleDelete = async (kind, id) => {
    if (kind === 'skill') {
      console.warn('TODO ParcoursPro: suppression de compétence non disponible (rattachée aux expériences)')
      return
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('parcoursPro.confirmDelete', 'Supprimer cet élément ?'))) return
    const response = await makePostRequest(
      urls.deleteBulkParcoursOperations,
      { selected: [id] },
      {},
      true,
    )
    if (response.status === 200) {
      fetchdata()
    }
  }

  const handleDuplicate = async (kind, id) => {
    if (kind === 'skill') {
      console.warn('TODO ParcoursPro: duplication de compétence non disponible')
      return
    }
    const row = findOperation(id)
    if (!row) return
    const response = await makePostRequest(
      urls.crudParcoursProOperations,
      buildDuplicatePayload(row),
      {},
      true,
    )
    if (response.status === 201) {
      fetchdata()
    }
  }

  // Ouvre les pages détails existantes (ExperienceDetails / FormationDetails)
  const handleOpen = (kind, id) => {
    navigate(kind === 'formation' ? `/parcourspro/formation/${id}` : `/parcourspro/experience/${id}`)
  }

  const handleExportCv = () => {
    const user = siteUser.user ?? {}
    const html = buildCvHtml(mapped, {
      name: [user.first_name, user.last_name].filter(Boolean).join(' '),
      email: user.email,
    })
    if (!openCvPrintWindow(html)) {
      // eslint-disable-next-line no-alert
      alert(t('parcoursPro.popupBlocked', "Autorise les popups pour exporter le CV."))
    }
  }
  const handleSuggestSkills = () => {
    console.warn('TODO ParcoursPro: suggestion de compétences (IA) non implémentée')
  }
  const handleAcceptSkill = () => {
    console.warn('TODO ParcoursPro: validation de compétence suggérée non implémentée')
  }

  return (
    <>
      <ParcoursPro
        experiences={mapped.experiences}
        formations={mapped.formations}
        skills={mapped.skills}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onDuplicate={handleDuplicate}
        onOpen={handleOpen}
        onExportCv={handleExportCv}
        onSuggestSkills={handleSuggestSkills}
        onAcceptSkill={handleAcceptSkill}
      />

      {/* Formulaire de création existant, piloté par openFromUrl.
          Son bouton déclencheur est masqué : l'ouverture passe par les
          boutons du nouveau composant (le Dialog MUI est rendu en portal). */}
      <div style={{ display: 'none' }}>
        <CreateOperationForm
          fetchdata={fetchdata}
          openFromUrl={createOpen}
          defaultParcoursType={createType}
          onUrlOpenConsumed={() => {
            setCreateOpen(false)
            const searchParams = new URLSearchParams(location.search)
            if (searchParams.get('action') || searchParams.get('modal')) {
              navigate(location.pathname, { replace: true })
            }
          }}
        />
      </div>

      {selected ? (
        <EditOperationForm
          setSelected={setSelected}
          params={selectedRowsParams}
          updateOperation={fetchdata}
        />
      ) : (
        ''
      )}
    </>
  )
}
